import { CheckIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { AlertCard, Button, Modal } from "@truevault/aquilo";
import clsx from "clsx";
import DOMPurify from "dompurify";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { WYSIWYGEditor } from "../../../../common/components/input/WYSIWYGEditor";
import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { DataRetentionModuleProps } from "./DataRetentionModule";
import { DATA_RETENTION_CATEGORIES } from "./dataRetentionCategories";
import {
  RetentionPolicyLayout,
  RetentionPolicyPreview,
  RetentionPreviewRow,
  usePreviewOpen,
  useTopBannerHeight,
} from "./RetentionPolicyPreview";
import { HighlightedPeriod, RetentionPurposeEditor } from "./RetentionPurposeEditor";
import {
  BUILTIN_ORDER,
  BUILTIN_STEPS,
  canChoosePeriod,
  CUSTOM_QUESTION,
  CUSTOM_STEP_KEY,
  draftDescription,
  GuidingQuestion,
  initialDraft,
  INTRO_APPROVED_SLUG,
  INTRO_QUESTION,
  INTRO_SLUG,
  INTRO_STEP_KEY,
  isDetailComplete,
  isDraftComplete,
  NEW_PURPOSE_KEY,
  picNamesInOrder,
  policyRowLabel,
  PurposeDraft,
  retentionProgress,
  TOTAL_STEPS,
} from "./retentionPolicySteps";

type StepStatus = "done" | "skipped" | "active" | "locked";

const CARD =
  "rounded-xl border border-solid border-neutral-200 bg-white shadow-[0px_1px_2px_0px_#1018280D]";

// The retention policy as a sequence of questions: approve one and the next opens, while a live
// preview on the right shows the policy section being built.
export function RetentionPolicyBuilder({
  header,
  collectedPic,
  dataRetention,
  unmappedItems,
  updateDataRetention,
  createDataRetention,
  removeDataRetention,
  refreshDataRetention,
  answers,
  setAnswer,
  setApproved,
  doneLabel,
}: DataRetentionModuleProps & { header: React.ReactNode; doneLabel?: string }) {
  const details = useMemo(() => dataRetention?.details ?? [], [dataRetention]);
  const byType = useMemo(
    () =>
      new Map(
        details
          .filter((detail) => detail.builtinType)
          .map((detail) => [detail.builtinType as DataRetentionPolicyDetailCategory, detail]),
      ),
    [details],
  );
  const customDetails = useMemo(() => details.filter((detail) => !detail.builtinType), [details]);

  const progress = retentionProgress(details, answers, unmappedItems.length);
  const activeKey = progress.activeKey;

  const [drafts, setDrafts] = useState<Record<string, PurposeDraft>>({});
  // Approved steps the user reopened to change.
  const [reopened, setReopened] = useState<Set<string>>(new Set());
  const [introDraft, setIntroDraft] = useState<string | null>(null);
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState<DataRetentionPolicyCategoryDto | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<DataRetentionPolicyCategoryDto | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = usePreviewOpen();
  const bannerHeight = useTopBannerHeight();

  // Bring the next question into view after the user approves, skips or restores a step — never
  // because data finished loading.
  const scrollOnAdvance = useRef(false);
  useEffect(() => {
    if (scrollOnAdvance.current) {
      scrollOnAdvance.current = false;
      const target = document.getElementById(stepElementId(activeKey));
      if (target) {
        target.style.scrollMarginTop = `${24 + bannerHeight}px`;
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const savedIntro = answers[INTRO_SLUG] ?? "";
  const isStepOpen = (key: string) => key === activeKey || reopened.has(key);
  const draftFor = (key: string, detail: DataRetentionPolicyCategoryDto | null) => {
    if (drafts[key]) {
      return drafts[key];
    }
    const draft = initialDraft(detail, collectedPic);
    // A new purpose starts with whatever no purpose covers yet, since that's usually why it's
    // being added.
    return key === NEW_PURPOSE_KEY ? { ...draft, pic: unmappedItems.map((pic) => pic.id) } : draft;
  };
  const setDraft = (key: string, draft: PurposeDraft) =>
    setDrafts((old) => ({ ...old, [key]: draft }));
  const closeStep = (key: string) => {
    setDrafts((old) => {
      const { [key]: _closed, ...rest } = old;
      return rest;
    });
    setReopened((old) => {
      const next = new Set(old);
      next.delete(key);
      return next;
    });
  };
  const reopen = (key: string) => setReopened((old) => new Set(old).add(key));

  const runSave = async (key: string, save: () => Promise<unknown>, advances: boolean) => {
    setSavingKey(key);
    try {
      await save();
      scrollOnAdvance.current = advances;
      await refreshDataRetention();
      return true;
    } catch {
      // The request hook already reported the error.
      scrollOnAdvance.current = false;
      return false;
    } finally {
      setSavingKey(null);
    }
  };

  const approvePurpose = async (key: string, detail: DataRetentionPolicyCategoryDto) => {
    const draft = draftFor(key, detail);
    const ok = await runSave(
      key,
      () =>
        updateDataRetention({
          id: detail.id,
          dto: {
            pic: draft.pic,
            description: draftDescription(detail.builtinType, draft),
            customLabel: detail.builtinType ? null : draft.label.trim(),
            retentionPeriod: draft.period,
            approved: true,
          },
        }),
      key === activeKey,
    );
    if (ok) {
      setApproved(detail.id, true);
      closeStep(key);
    }
  };

  const createPurpose = async () => {
    const draft = draftFor(NEW_PURPOSE_KEY, null);
    const ok = await runSave(
      NEW_PURPOSE_KEY,
      () =>
        createDataRetention({
          dto: {
            builtinType: null,
            customLabel: draft.label.trim(),
            description: draftDescription(null, draft),
            retentionPeriod: draft.period,
            pic: draft.pic,
            approved: true,
          },
        }),
      false,
    );
    if (ok) {
      closeStep(NEW_PURPOSE_KEY);
      setIsAdding(false);
    }
  };

  const skipPurpose = async (detail: DataRetentionPolicyCategoryDto) => {
    setConfirmSkip(null);
    const key = detail.builtinType ?? detail.id;
    if (await runSave(key, () => removeDataRetention({ id: detail.id }), key === activeKey)) {
      closeStep(key);
    }
  };

  const restorePurpose = (type: DataRetentionPolicyDetailCategory) =>
    runSave(
      type,
      () =>
        createDataRetention({
          dto: {
            builtinType: type,
            description: "",
            customLabel: null,
            retentionPeriod: canChoosePeriod(type) ? "YEARS_7" : "CUSTOM",
            pic: [],
            approved: false,
          },
        }),
      true,
    );

  const approveIntro = async () => {
    setSavingKey(INTRO_STEP_KEY);
    try {
      if (introDraft != null && introDraft !== savedIntro) {
        await setAnswer(INTRO_SLUG, introDraft);
      }
      if (answers[INTRO_APPROVED_SLUG] !== "true") {
        scrollOnAdvance.current = !progress.isIntroDone;
        await setAnswer(INTRO_APPROVED_SLUG, "true");
      }
      setIntroDraft(null);
      setIsEditingIntro(false);
      closeStep(INTRO_STEP_KEY);
    } catch {
      // reported by the survey hook
      scrollOnAdvance.current = false;
    } finally {
      setSavingKey(null);
    }
  };

  // ----- preview -----
  const previewRows: RetentionPreviewRow[] = [];
  const addRow = (
    key: string,
    detail: DataRetentionPolicyCategoryDto,
    type: DataRetentionPolicyDetailCategory | null,
  ) => {
    if (isStepOpen(key)) {
      const draft = draftFor(key, detail);
      previewRows.push({
        key,
        label: policyRowLabel(type, draft.label, picNamesInOrder(draft.pic, collectedPic)),
        description: draftDescription(type, draft),
        isDraft: true,
      });
    } else if (isDetailComplete(detail)) {
      previewRows.push({
        key,
        label: policyRowLabel(
          type,
          detail.customLabel ?? "",
          picNamesInOrder(detail.pic, collectedPic),
        ),
        description: detail.description,
        isDraft: false,
      });
    }
  };
  BUILTIN_ORDER.forEach((type) => {
    const detail = byType.get(type);
    if (detail) {
      addRow(type, detail, type);
    }
  });
  const customIsReached = activeKey === CUSTOM_STEP_KEY;
  customDetails.forEach((detail) => {
    const open = customIsReached && (!isDetailComplete(detail) || reopened.has(detail.id));
    if (open) {
      const draft = draftFor(detail.id, detail);
      previewRows.push({
        key: detail.id,
        label: policyRowLabel(null, draft.label, picNamesInOrder(draft.pic, collectedPic)),
        description: draftDescription(null, draft),
        isDraft: true,
      });
    } else if (isDetailComplete(detail)) {
      addRow(detail.id, detail, null);
    }
  });
  if (isAdding) {
    const draft = draftFor(NEW_PURPOSE_KEY, null);
    previewRows.push({
      key: NEW_PURPOSE_KEY,
      label: policyRowLabel(null, draft.label, picNamesInOrder(draft.pic, collectedPic)),
      description: draftDescription(null, draft),
      isDraft: true,
    });
  }

  // ----- steps -----
  const statusOf = (key: string, index: number): StepStatus => {
    if (key === INTRO_STEP_KEY) {
      return progress.isIntroDone ? "done" : "active";
    }
    const detail = byType.get(key as DataRetentionPolicyDetailCategory);
    if (!detail) {
      return "skipped";
    }
    if (isDetailComplete(detail)) {
      return "done";
    }
    return index + 1 === progress.activeNumber ? "active" : "locked";
  };

  const introStep = (() => {
    const status = statusOf(INTRO_STEP_KEY, 0);
    const open = isStepOpen(INTRO_STEP_KEY);
    const shownIntro = introDraft ?? savedIntro;
    return (
      <StepFrame
        key={INTRO_STEP_KEY}
        stepKey={INTRO_STEP_KEY}
        number={1}
        status={open ? "active" : status}
        question={INTRO_QUESTION}
        subtitle="It opens the retention section of your privacy policy. Save it as is, or edit the text."
      >
        {open ? (
          <div className={CARD}>
            <div className="px-[24px] py-[20px]">
              {isEditingIntro ? (
                <WYSIWYGEditor
                  height="120px"
                  rows={3}
                  toolbar={false}
                  defaultValue={shownIntro}
                  onChange={setIntroDraft}
                />
              ) : (
                <>
                  <IntroQuote html={shownIntro} />
                  <button
                    type="button"
                    className="mt-[12px] cursor-pointer border-0 bg-transparent p-0 text-[13px] font-medium text-primary-600 hover:underline"
                    onClick={() => setIsEditingIntro(true)}
                  >
                    Edit statement
                  </button>
                </>
              )}
            </div>
            <StepCardFooter
              hint="Save it to add it to your policy."
              isReopened={reopened.has(INTRO_STEP_KEY)}
              canApprove={(introDraft ?? savedIntro).replace(/<[^>]*>/g, "").trim().length > 0}
              isSaving={savingKey === INTRO_STEP_KEY}
              onCancel={() => {
                setIntroDraft(null);
                setIsEditingIntro(false);
                closeStep(INTRO_STEP_KEY);
              }}
              onApprove={approveIntro}
            />
          </div>
        ) : (
          <SummaryCard
            title="Intro statement"
            onEdit={() => {
              reopen(INTRO_STEP_KEY);
              setIsEditingIntro(true);
            }}
          >
            <IntroQuote html={savedIntro} />
          </SummaryCard>
        )}
      </StepFrame>
    );
  })();

  const purposeSteps = BUILTIN_ORDER.map((type, i) => {
    const index = i + 1;
    const detail = byType.get(type) ?? null;
    const status = statusOf(type, index);
    const open = detail != null && isStepOpen(type) && status !== "locked";
    const draft = open ? draftFor(type, detail) : null;
    const step = BUILTIN_STEPS[type];

    let body: React.ReactNode = null;
    if (status === "skipped") {
      body = (
        <div className="flex items-center justify-between gap-[16px] rounded-xl border border-dashed border-neutral-300 bg-white px-[24px] py-[14px]">
          <div className="min-w-0">
            <div className="text-[14px] font-medium leading-[20px] text-neutral-700">
              {step.title} doesn’t apply
            </div>
            <div className="text-[13px] leading-[20px] text-neutral-500">
              It won’t appear in your privacy policy.
            </div>
          </div>
          <Button
            color="link"
            size="xs"
            disabled={savingKey === type}
            onClick={() => restorePurpose(type)}
          >
            {savingKey === type ? "Adding…" : "Add it back"}
          </Button>
        </div>
      );
    } else if (open && detail && draft) {
      body = (
        <div className={CARD}>
          <PurposeCardHeader
            title={step.title}
            policyLabel={DATA_RETENTION_CATEGORIES[type].label}
            action={
              <button
                type="button"
                className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[14px] font-medium text-neutral-600 hover:text-neutral-900 hover:underline"
                onClick={() =>
                  detail.pic.length > 0 ? setConfirmSkip(detail) : skipPurpose(detail)
                }
              >
                Doesn’t apply to us
              </button>
            }
          />
          <div className="px-[24px] py-[24px]">
            <RetentionPurposeEditor
              type={type}
              draft={draft}
              onChange={(next) => setDraft(type, next)}
              collectedPic={collectedPic}
              controlPrefix={`retention-${type}`}
            />
          </div>
          <StepCardFooter
            hint={
              draft.pic.length === 0
                ? "Select at least one category, or mark this as not applying to you."
                : "Save it to add this row to your policy."
            }
            isReopened={reopened.has(type)}
            canApprove={isDraftComplete(type, draft)}
            isSaving={savingKey === type}
            onCancel={() => closeStep(type)}
            onApprove={() => approvePurpose(type, detail)}
          />
        </div>
      );
    } else if (status === "done" && detail) {
      body = (
        <PurposeSummary
          title={step.title}
          picNames={picNamesInOrder(detail.pic, collectedPic)}
          description={detail.description}
          onEdit={() => reopen(type)}
        />
      );
    }

    return (
      <StepFrame
        key={type}
        stepKey={type}
        number={index + 1}
        status={open ? "active" : status}
        question={step.question}
        subtitle={
          canChoosePeriod(type)
            ? "Select the data you collect for this, then choose how long you keep it."
            : "Select the data you collect for this, then review how long you keep it."
        }
      >
        {body}
      </StepFrame>
    );
  });

  const customStatus: StepStatus = !customIsReached
    ? "locked"
    : progress.isComplete && !isAdding
    ? "done"
    : "active";
  const customStep = (
    <StepFrame
      key={CUSTOM_STEP_KEY}
      stepKey={CUSTOM_STEP_KEY}
      number={TOTAL_STEPS}
      status={customStatus}
      question={CUSTOM_QUESTION}
      subtitle="Add a purpose for anything the questions above didn’t cover, like warranty claims or a loyalty program. Each one becomes its own row in your policy."
      isLast
    >
      {customIsReached && (
        <div className="flex flex-col gap-[16px]">
          {unmappedItems.length > 0 && (
            <AlertCard variant="warning" containerClasses="!mt-0 !mb-0">
              <div>
                <div className="font-medium">Some data you collect isn’t covered yet</div>
                <div className="mt-[4px]">
                  Every category you collect needs a retention period. Add these to a purpose above,
                  or add a purpose for them here:
                </div>
                <div className="mt-[8px] flex flex-wrap gap-[6px]">
                  {unmappedItems.map((pic) => (
                    <DataChip key={pic.id} name={pic.name} />
                  ))}
                </div>
              </div>
            </AlertCard>
          )}

          {customDetails.map((detail) => {
            const open = !isDetailComplete(detail) || reopened.has(detail.id);
            if (!open) {
              return (
                <PurposeSummary
                  key={detail.id}
                  title={detail.customLabel || "Untitled purpose"}
                  picNames={picNamesInOrder(detail.pic, collectedPic)}
                  description={detail.description}
                  onEdit={() => reopen(detail.id)}
                />
              );
            }
            const draft = draftFor(detail.id, detail);
            return (
              <div key={detail.id} className={CARD}>
                <PurposeCardHeader
                  title={draft.label.trim() || "Untitled purpose"}
                  action={
                    <RemoveButton
                      label="Remove this purpose"
                      onClick={() => setConfirmRemove(detail)}
                    />
                  }
                />
                <div className="px-[24px] py-[24px]">
                  <RetentionPurposeEditor
                    type={null}
                    draft={draft}
                    onChange={(next) => setDraft(detail.id, next)}
                    collectedPic={collectedPic}
                    controlPrefix={`retention-${detail.id}`}
                  />
                </div>
                <StepCardFooter
                  hint="Save it to add this row to your policy."
                  isReopened={reopened.has(detail.id)}
                  canApprove={isDraftComplete(null, draft)}
                  isSaving={savingKey === detail.id}
                  onCancel={() => closeStep(detail.id)}
                  onApprove={() => approvePurpose(detail.id, detail)}
                />
              </div>
            );
          })}

          {isAdding && (
            <div className={CARD}>
              <PurposeCardHeader
                title="New retention purpose"
                action={
                  <RemoveButton
                    label="Discard this purpose"
                    onClick={() => {
                      closeStep(NEW_PURPOSE_KEY);
                      setIsAdding(false);
                    }}
                  />
                }
              />
              <div className="px-[24px] py-[24px]">
                <RetentionPurposeEditor
                  type={null}
                  draft={draftFor(NEW_PURPOSE_KEY, null)}
                  onChange={(next) => setDraft(NEW_PURPOSE_KEY, next)}
                  collectedPic={collectedPic}
                  controlPrefix="retention-new"
                />
              </div>
              <StepCardFooter
                hint="Name the purpose and select its data to save it."
                isReopened
                cancelLabel="Discard"
                canApprove={isDraftComplete(null, draftFor(NEW_PURPOSE_KEY, null))}
                isSaving={savingKey === NEW_PURPOSE_KEY}
                onCancel={() => {
                  closeStep(NEW_PURPOSE_KEY);
                  setIsAdding(false);
                }}
                onApprove={createPurpose}
              />
            </div>
          )}

          {!isAdding && (
            <div>
              <Button color="secondary" size="s" onClick={() => setIsAdding(true)}>
                <span className="flex items-center gap-[6px]">
                  <PlusIcon className="h-[16px] w-[16px]" />
                  Add a retention purpose
                </span>
              </Button>
            </div>
          )}

          {progress.isComplete && !isAdding && (
            <div className="flex items-center gap-[8px] text-[14px] leading-[20px] text-emerald-700">
              <CheckCircleIcon className="h-[20px] w-[20px] shrink-0" />
              Every category you collect has a retention period.
              {doneLabel && ` Click ${doneLabel} to finish.`}
            </div>
          )}
        </div>
      )}
    </StepFrame>
  );

  const preview = (
    <RetentionPolicyPreview
      introHtml={introDraft ?? savedIntro}
      isIntroDraft={isStepOpen(INTRO_STEP_KEY)}
      rows={previewRows}
      onHide={() => setIsPreviewOpen(false)}
    />
  );

  return (
    <>
      <RetentionPolicyLayout
        main={
          <>
            {header}
            <div className="mt-[32px]">
              {introStep}
              {purposeSteps}
              {customStep}
            </div>
          </>
        }
        preview={preview}
        isPreviewOpen={isPreviewOpen}
        onShowPreview={() => setIsPreviewOpen(true)}
      />

      {confirmSkip && (
        <Modal
          title="Mark this purpose as not applying to you?"
          body="It will be removed from your privacy policy, along with the data and retention period you chose for it."
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setConfirmSkip(null)}
          secondButton="Yes, it doesn’t apply"
          secondButtonColor="warning"
          onSecondButtonClick={() => skipPurpose(confirmSkip)}
          showOverlay
          onClose={() => setConfirmSkip(null)}
        />
      )}
      {confirmRemove && (
        <Modal
          title="Remove this retention purpose?"
          body="It will be removed from your privacy policy."
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setConfirmRemove(null)}
          secondButton="Remove"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            const detail = confirmRemove;
            setConfirmRemove(null);
            skipPurpose(detail);
          }}
          showOverlay
          onClose={() => setConfirmRemove(null)}
        />
      )}
    </>
  );
}

const stepElementId = (key: string) => `retention-step-${key}`;
// Space between a step badge and the rail line on either side.
const RAIL_GAP = 4;

function StepFrame({
  stepKey,
  number,
  status,
  question,
  subtitle,
  isLast = false,
  children,
}: {
  stepKey: string;
  number: number;
  status: StepStatus;
  question: GuidingQuestion;
  subtitle: string;
  isLast?: boolean;
  children?: React.ReactNode;
}) {
  const isLocked = status === "locked";
  return (
    <section
      id={stepElementId(stepKey)}
      className="flex gap-[16px]"
      aria-current={status === "active" ? "step" : undefined}
    >
      <div className="relative w-[28px] shrink-0">
        <StepBadge number={number} status={status} />
        {/* Spans badge to badge with the same gap at both ends: the next step's badge sits at the
            top of the next section, flush with this section's bottom. */}
        {!isLast && (
          <div
            aria-hidden
            className="absolute rounded-full bg-neutral-300"
            style={{ left: 13, width: 2, top: 28 + RAIL_GAP, bottom: RAIL_GAP }}
          />
        )}
      </div>
      <div
        className={clsx("min-w-0 flex-1", isLast ? "pb-0" : isLocked ? "pb-[24px]" : "pb-[40px]")}
      >
        <h2
          className={clsx(
            "mb-0 mt-[1px] text-[18px] font-semibold leading-[26px]",
            isLocked ? "text-neutral-400" : "text-neutral-900",
          )}
        >
          {question.before}
          <span className={isLocked ? undefined : "text-primary-700"}>{question.highlight}</span>
          {question.after}
        </h2>
        {!isLocked && (
          <p className="mb-0 mt-[4px] text-[14px] leading-[20px] text-neutral-500">{subtitle}</p>
        )}
        {children && <div className="mt-[16px]">{children}</div>}
      </div>
    </section>
  );
}

function StepBadge({ number, status }: { number: number; status: StepStatus }) {
  return (
    <div
      className={clsx(
        "flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full text-[13px] font-semibold transition-colors duration-200",
        status === "done" && "bg-emerald-100 text-emerald-700",
        status === "skipped" && "bg-neutral-100 text-neutral-400",
        status === "active" && "bg-primary-600 text-white ring-4 ring-primary-100",
        status === "locked" && "border border-solid border-neutral-300 bg-white text-neutral-400",
      )}
      aria-label={
        status === "done"
          ? `Step ${number}, saved`
          : status === "skipped"
          ? `Step ${number}, doesn’t apply`
          : `Step ${number}`
      }
    >
      {status === "done" ? (
        <CheckIcon className="h-[16px] w-[16px]" />
      ) : status === "skipped" ? (
        <MinusIcon className="h-[16px] w-[16px]" />
      ) : (
        number
      )}
    </div>
  );
}

function PurposeCardHeader({
  title,
  policyLabel,
  action,
}: {
  title: string;
  policyLabel?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-[16px] border-b border-solid border-neutral-200 px-[24px] py-[16px]">
      <div className="min-w-0">
        <div className="text-[16px] font-semibold leading-[24px] text-neutral-900">{title}</div>
        {policyLabel && (
          <div className="mt-[2px] text-[13px] leading-[20px] text-neutral-500">
            Listed in your policy as “{policyLabel}”
          </div>
        )}
      </div>
      {action}
    </div>
  );
}

function StepCardFooter({
  hint,
  isReopened,
  cancelLabel = "Cancel",
  canApprove,
  isSaving,
  onCancel,
  onApprove,
}: {
  hint: string;
  isReopened: boolean;
  cancelLabel?: string;
  canApprove: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onApprove: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-[16px] rounded-b-xl border-t border-solid border-neutral-200 bg-neutral-0 px-[24px] py-[14px]">
      <div className="min-w-0 text-[13px] leading-[20px] text-neutral-500">{hint}</div>
      <div className="flex shrink-0 items-center gap-[12px]">
        {isReopened && (
          <Button color="tertiary" size="s" disabled={isSaving} onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        <Button color="primary" size="s" disabled={!canApprove || isSaving} onClick={onApprove}>
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={clsx(CARD, "px-[24px] py-[16px]")}>
      <div className="flex items-center justify-between gap-[12px]">
        <span className="min-w-0 truncate text-[16px] font-semibold leading-[24px] text-neutral-900">
          {title}
        </span>
        <Button color="link" size="xs" onClick={onEdit}>
          Edit
        </Button>
      </div>
      <div className="mt-[12px]">{children}</div>
    </div>
  );
}

function PurposeSummary({
  title,
  picNames,
  description,
  onEdit,
}: {
  title: string;
  picNames: string[];
  description: string;
  onEdit: () => void;
}) {
  return (
    <SummaryCard title={title} onEdit={onEdit}>
      <div className="flex flex-col gap-[10px]">
        <div className="flex gap-[16px]">
          <div className="w-[72px] shrink-0 text-[13px] leading-[22px] text-neutral-500">Data</div>
          <div className="flex min-w-0 flex-wrap gap-[6px]">
            {picNames.map((name) => (
              <DataChip key={name} name={name} />
            ))}
          </div>
        </div>
        <div className="flex gap-[16px]">
          <div className="w-[72px] shrink-0 text-[13px] leading-[20px] text-neutral-500">
            Kept for
          </div>
          <div className="min-w-0 text-[14px] leading-[20px] text-neutral-700">
            <HighlightedPeriod text={description.trim()} />
          </div>
        </div>
      </div>
    </SummaryCard>
  );
}

function DataChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-solid border-neutral-200 bg-neutral-50 px-[10px] py-[2px] text-[12px] font-medium leading-[18px] text-neutral-700">
      {name}
    </span>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-transparent p-0 text-neutral-500 hover:bg-red-50 hover:text-red-700"
    >
      <TrashIcon className="h-[18px] w-[18px]" />
    </button>
  );
}

function IntroQuote({ html }: { html: string }) {
  // Inline formatting only, so the editor's <p> wrapper doesn't bring browser margins along.
  const clean = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "u", "br"],
        ALLOWED_ATTR: ["href", "target", "rel"],
      }),
    [html],
  );
  return (
    <div
      className="border-0 border-l-[3px] border-solid border-primary-200 pl-[16px] text-[15px] leading-[24px] text-neutral-800"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
